import { Space, Spin, Tabs, Typography } from 'antd';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import { FileTextOutlined, WarningFilled } from '@ant-design/icons';
import useBreadcrumb from '../../hooks/useBreadcrumb';
import useDeclarations from '../../hooks/useDeclarations';
import useFormUtils from '../../hooks/useFormUtils';
import useGetDeclarationMapValues from '../../hooks/useGetDeclarationMapValues';
import { barcodeDisabled } from './uk/gvms/components/barcode/BarcodeView';
import {
    declarationIsCompleted,
    disableSaveOrSubmitDeclarationButton,
    externalEntityMap,
    getDeclarationFieldName,
} from './utils/declaration-utils';
import CustomsRequestsTabLabel from './declaration-tabs/customs-requests/ics2/CustomsRequestsTabLabel';
import DeclarationTabKeys from './utils/DeclarationTabKeys';
import { getDeclarationFormTabKeys } from './utils/getDeclarationFormTabKeys';
import useRefetchDeclarationOnTabChangeIfAmendable from './declaration-tabs/declaration-form/utils/useRefetchDeclarationOnTabChangeIfAmendable';
import { getDeclarationActiveTabKey } from './declaration-tabs/declaration-form/utils/getDeclarationActiveTabKey';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { Events } from 'utils/events.enum';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import Tag from 'components/new-ui/base/tag/Tag';
import DeclarationStatusTag from 'components/ui/composed/declarations/declaration-status/DeclarationStatusTag';
import { getTemplate } from 'store/declarations/client';
import { Template } from 'store/template/template';
import { getDeclarationTitle } from 'views/custom-declaration/declaration-table/DeclarationColumns';
import { useTranslation } from 'react-i18next';
import { getDeclarationPageHeaderTitle } from './declaration-tabs/declaration-form/utils/getDeclarationPageTitle';
import useManifests from 'hooks/useManifests';
import useJobs from 'hooks/useJobs';
import { hasPermissions } from 'config/roles/hasPermissions';
import useSession from 'hooks/useSession';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import DeclarationSubmittedModal from './declaration-tabs/declaration-form/components/DeclarationSubmittedModal';
import { getJob } from 'store/jobs/client';
import { DeclarationFieldName } from 'views/declarations/common/declarationType';
import { StyledButton, StyledHeader, StyledLayout, StyledTabs } from './declaration-tabs/declaration-form/Form.styles';
import CancelDeclarationModalButton from 'views/declarations/common/CancelDeclarationModal';
import TransportDetailsDiscrepenciesWarning from './TransportDetailsDiscrepenciesWarning';
import MirroredDeclarationsLinksList from './mirroring/MirroredDeclarationsLinksList';
import useMirroredDeclarations from './mirroring/useMirroredDeclarations';
import GenerateMirroredDeclarationButton from './mirroring/GenerateMirroredDeclarationButton';
import { ManifestTabKeys } from 'views/manifests/manifest-details/types';

const { TabPane } = Tabs;

interface Props {
    children: ReactNode;
}

const DeclarationPage = ({ children }: Props) => {
    const { declarationId, manifestId, jobId } = useParams<{
        declarationId?: string;
        productId?: string;
        manifestId?: string;
        jobId?: string;
    }>();
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const location = useLocation();
    const navigate = useNavigate();
    const [isSavingDeclaration, setIsSavingDeclaration] = useState(false);
    const [declarationTemplate, setDeclarationTemplate] = useState<Template>();

    const { isAis, isICS2 } = useFormUtils();
    const { t } = useTranslation('common');
    const { ...mapper } = useGetDeclarationMapValues();
    const { userInfo } = useSession();

    const { manifest, getManifestById } = useManifests();
    const { job } = useJobs({ jobId });

    const { getDeclaration, declaration, cancelCdsDeclaration } = useDeclarations();
    const { declarationName } = useGetDeclarationMapValues();

    const isManifests = Boolean(manifestId && jobId);
    const baseUrl = getDeclarationUrl({ manifestId, jobId, declarationId });
    const pageTitle = getDeclarationPageHeaderTitle(declaration);
    const { mirroredDeclarations, isLoading: areMirroredDeclarationsLoading } = useMirroredDeclarations(declaration);

    const [declarationIsFetching, setDeclarationIsFetching] = useState<boolean>(false);
    const [submittedModalOpen, setSubmittedModalOpen] = useState(false);

    const activeTabKey = useMemo(() => getDeclarationActiveTabKey(location, isManifests), [location, isManifests]);

    const isInFormTab = useMemo(() => {
        return activeTabKey === DeclarationTabKeys.FORM || activeTabKey === DeclarationTabKeys.VIEW_ONLY;
    }, [activeTabKey]);

    // ICS2 ENS master items and house consignment items are edited as products, so they get the
    // item level actions instead of the master level ones. Their list routes are left untouched.
    const isInItemEditor = /\/(?:master-items\/[^/]+|house-consignments\/[^/]+\/[^/]+)$/.test(location.pathname);
    const isInProductsFormTab = location.pathname.includes('/products') || isInItemEditor;
    const declarationsProductMatch = useMatch('declarations/:declarationId/products/:productId');
    const manifestsProductMatch = useMatch('manifests/:manifestId/:jobId/:declarationId/products/:productId');
    const inProductView = Boolean(declarationsProductMatch || manifestsProductMatch);
    const canUpdateDeclarations = hasPermissions(userInfo, 'declarations', 'UPDATE');
    const isH1Ais = getDeclarationFieldName(declaration) === DeclarationFieldName.IE_IMPORT_H1;

    const fetchDeclaration = async (declarationId: string) => {
        setDeclarationIsFetching(true);
        try {
            await getDeclaration(declarationId);
        } catch (err) {
            throw err;
        } finally {
            setDeclarationIsFetching(false);
        }
    };

    const handleSubmit = () => {
        window.EventBus.publish(Events.TRIGGER_SUBMIT_DECLARATION);
    };

    const handleSaveAsDraft = () => {
        window.EventBus.publish(Events.TRIGGER_SAVE_DECLARATION_AS_DRAFT);
    };

    const handleSaveProduct = () => {
        window.EventBus.publish(Events.TRIGGER_SAVE_PRODUCT);
    };

    const handleCancelSaveProduct = () => {
        window.EventBus.publish(Events.TRIGGER_CANCEL_SAVE_PRODUCT);
    };

    const currentDeclaration = useMemo(
        () => declaration?.[mapper.declarationType],
        [declaration, mapper.declarationType]
    );
    const disabledSaveOrSubmit = useMemo(
        () => (declaration?.status ? disableSaveOrSubmitDeclarationButton(declaration.status) : false),
        [declaration?.status]
    );

    const disabledFormActions =
        currentDeclaration &&
        typeof currentDeclaration === 'object' &&
        'userInvalidationSubmitted' in currentDeclaration &&
        !!currentDeclaration.userInvalidationSubmitted;

    const submitButtonEl = useMemo(
        () =>
            !disabledSaveOrSubmit && (
                <StyledButton primary type="primary" onClick={handleSubmit}>
                    {declaration?.gvmsDeclaration ? 'Submit GMR' : t('footer.submit')}
                </StyledButton>
            ),
        [declaration?.gvmsDeclaration, disabledSaveOrSubmit, t]
    );

    const formActionButtons = useMemo(() => {
        if (!canUpdateDeclarations) return null;

        // Note: mirroring is offered on the master form only - the preparation flow saves the
        // declaration but not the product being edited, so it would silently drop those changes.
        return isInProductsFormTab ? (
            <Space size={6}>
                {inProductView && (
                    <StyledButton onClick={handleCancelSaveProduct}>{t('footer.backToProducts')}</StyledButton>
                )}

                {!disabledFormActions && (
                    <StyledButton loading={isSavingDeclaration} disabled={false} onClick={handleSaveProduct}>
                        {t('footer.saveProduct')}
                    </StyledButton>
                )}

                {!declaration?.preBoardingNotification && submitButtonEl}
            </Space>
        ) : (
            <Space size={6}>
                {isH1Ais && !disabledFormActions && !declaration?.archived && (
                    <GenerateMirroredDeclarationButton
                        declaration={declaration}
                        manifestId={manifestId}
                        jobId={jobId}
                    />
                )}

                {!disabledFormActions && (
                    <CancelDeclarationModalButton
                        declaration={declaration}
                        cancelCdsDeclarationRequest={cancelCdsDeclaration}
                        newUi
                    />
                )}
                {!disabledFormActions && !disabledSaveOrSubmit && (
                    <StyledButton
                        disabled={disabledSaveOrSubmit}
                        loading={isSavingDeclaration}
                        onClick={handleSaveAsDraft}
                    >
                        {t(declaration?.status === 'DRAFT' ? 'footer.saveDraft' : 'footer.save')}
                    </StyledButton>
                )}

                {!declaration?.preBoardingNotification && submitButtonEl}
            </Space>
        );
    }, [
        isH1Ais,
        manifestId,
        jobId,
        cancelCdsDeclaration,
        canUpdateDeclarations,
        declaration,
        disabledFormActions,
        disabledSaveOrSubmit,
        inProductView,
        isInProductsFormTab,
        isSavingDeclaration,
        submitButtonEl,
        t,
    ]);

    // Subscribe to action events to handle buttons loading state
    useEffect(() => {
        const savingDeclarationStartSubscriberId = window.EventBus.subscribe(Events.SAVING_DECLARATION_START, () => {
            setIsSavingDeclaration(true);
        });
        const savingDeclarationEndSubscriberId = window.EventBus.subscribe(Events.SAVING_DECLARATION_END, () => {
            setIsSavingDeclaration(false);
        });
        const declarationSubmittedSuccessSubscriberId = window.EventBus.subscribe(
            Events.DECLARATION_SUBMITTED_SUCCESS,
            () => setSubmittedModalOpen(true)
        );

        return () => {
            window.EventBus.unsubscribe(savingDeclarationStartSubscriberId);
            window.EventBus.unsubscribe(savingDeclarationEndSubscriberId);
            window.EventBus.unsubscribe(declarationSubmittedSuccessSubscriberId);
        };
    }, []);

    useRefetchDeclarationOnTabChangeIfAmendable({
        activeTabKey,
    });

    useEffect(() => {
        if (declarationId) {
            fetchDeclaration(declarationId).catch((err) => {
                // Navigate the user away if we can not get the declaration data
                const errorMessage = getErrorMessage(err);

                Notification({
                    type: 'error',
                    messageTitle: 'Error',
                    description: errorMessage || 'Something Went wrong',
                    placement: 'top',
                });

                navigate(-1);
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [declarationId]);

    // Fetch manifest when viewing declaration under manifest (e.g. direct URL) so breadcrumb shows referenceId
    useEffect(() => {
        if (manifestId) {
            getManifestById(manifestId);
        }
    }, [manifestId, getManifestById]);

    // Get template name for the declaration
    useEffect(() => {
        if (!declaration?.templateId) {
            setDeclarationTemplate(undefined);
            return;
        }

        let cancelled = false;
        setDeclarationTemplate(undefined);
        getTemplate(declaration.templateId).then((res) => {
            if (!cancelled) setDeclarationTemplate(res);
        });

        return () => {
            cancelled = true;
        };
    }, [declaration?.templateId]);

    useEffect(() => {
        const crumbs: { breadcrumbName: string; path: string }[] = [];

        if (isManifests) {
            crumbs.push(
                {
                    breadcrumbName: 'Home',
                    path: '/home',
                },
                { breadcrumbName: 'Manifests', path: '../manifests' },
                {
                    breadcrumbName: manifest?.referenceId ?? '...',
                    path: `../manifests/${manifestId}`,
                },
                {
                    breadcrumbName: job?.referenceId ?? '...',
                    path: `../../manifests/${manifestId}/${jobId}`,
                },
                { breadcrumbName: declaration?.mrn ?? pageTitle, path: '' }
            );
        } else {
            const secondBreadcrumbName = !declaration?.declarationExternalEntity
                ? ''
                : externalEntityMap[declaration.declarationExternalEntity];

            crumbs.push(
                { breadcrumbName: 'Declarations', path: '' },
                { breadcrumbName: `${secondBreadcrumbName} Customs`, path: '' },
                {
                    breadcrumbName: !declarationIsFetching ? getDeclarationTitle(declaration) : '',
                    path: '',
                }
            );
        }

        setBreadcrumbRoutes(crumbs);
    }, [
        declaration?.declarationExternalEntity,
        setBreadcrumbRoutes,
        declarationIsFetching,
        declaration,
        isManifests,
        manifestId,
        jobId,
        declarationId,
        manifest?.referenceId,
        job?.referenceId,
        pageTitle,
    ]);

    const formTabName = useMemo(() => {
        if (declarationIsCompleted(declaration)) return 'Summary';
        return `${declarationName} Form`;
    }, [declaration, declarationName]);

    const handleDeclarationFormTabs = useMemo(() => {
        const tabKeys = getDeclarationFormTabKeys(declaration);

        return (
            <>
                {tabKeys.map((tabKey) => (
                    <TabPane
                        tab={tabKey === DeclarationTabKeys.VIEW_ONLY ? 'Form Information' : formTabName}
                        key={tabKey}
                    />
                ))}
            </>
        );
    }, [declaration, formTabName]);

    const handleBackButtonClick = () => {
        navigate(-1);
    };

    if (declarationIsFetching || getDeclarationActiveTabKey(location, isManifests) !== activeTabKey)
        return (
            <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <Spin size="large" tip="Loading declaration..." />
            </div>
        );

    return (
        <>
            <StyledLayout>
                <StyledHeader>
                    <PageHeader
                        title={pageTitle}
                        icon={null}
                        showBackButton
                        onBackButtonClick={handleBackButtonClick}
                        additionalContent={
                            <Space size={16} align="center" style={{ marginLeft: 16 }}>
                                {declaration?.status && <DeclarationStatusTag status={declaration.status} />}
                                {declarationTemplate && (
                                    <Space size={4}>
                                        <FileTextOutlined />
                                        <Typography.Text>{declarationTemplate.templateName}</Typography.Text>
                                    </Space>
                                )}
                                {declaration?.archived && <Tag status="default" label="Archived" />}
                                <MirroredDeclarationsLinksList
                                    declarations={mirroredDeclarations}
                                    isLoading={areMirroredDeclarationsLoading}
                                    manifestId={manifestId}
                                    jobId={jobId}
                                />
                            </Space>
                        }
                        sidebar={isInFormTab && formActionButtons}
                    />

                    {declaration?.manifestDetailsMismatch && <TransportDetailsDiscrepenciesWarning />}

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <StyledTabs
                            onChange={(k) => {
                                const tab = k as DeclarationTabKeys;
                                if (tab === DeclarationTabKeys.FORM) {
                                    navigate(baseUrl, {
                                        state: { activeTabKey: tab },
                                    });
                                } else {
                                    navigate(`${baseUrl}/${tab}`, {
                                        state: { activeTabKey: tab },
                                    });
                                }
                            }}
                            activeKey={activeTabKey}
                        >
                            {handleDeclarationFormTabs}
                            {declaration?.gvmsDeclaration || declaration?.preBoardingNotification ? (
                                <TabPane
                                    tab="Barcode"
                                    key={DeclarationTabKeys.BARCODE}
                                    disabled={barcodeDisabled(declaration)}
                                />
                            ) : (
                                <>
                                    <TabPane tab="Documentation" key={DeclarationTabKeys.DOCUMENTATION} />
                                    <TabPane tab="Customer Details" key={DeclarationTabKeys.CUSTOMER_DETAILS} />
                                </>
                            )}
                            <TabPane tab="Notes" key={DeclarationTabKeys.NOTES} />
                            {isICS2 && (
                                <TabPane
                                    tab={
                                        <CustomsRequestsTabLabel
                                            declarationId={declarationId}
                                            refreshKey={`${activeTabKey}:${declaration?.updatedAt ?? ''}`}
                                        />
                                    }
                                    key={DeclarationTabKeys.CUSTOMS_REQUESTS}
                                />
                            )}
                            {!declaration?.preBoardingNotification && (
                                <TabPane tab="History" key={DeclarationTabKeys.HISTORY} />
                            )}
                            {isAis && declaration?.status !== 'DRAFT' && (
                                <TabPane tab="Refunds" key={DeclarationTabKeys.REFUNDS} />
                            )}
                            {isH1Ais && (
                                <TabPane
                                    tab={
                                        <Space>
                                            <Typography.Text>VAT & Duty</Typography.Text>
                                            {declaration?.taxCalculationDiffers ? (
                                                <WarningFilled
                                                    style={{
                                                        color: 'var(--error-normal)',
                                                    }}
                                                />
                                            ) : undefined}
                                        </Space>
                                    }
                                    key={DeclarationTabKeys.VAT_AND_DUTY}
                                />
                            )}
                        </StyledTabs>
                    </div>
                </StyledHeader>

                {children}
            </StyledLayout>

            <DeclarationSubmittedModal
                open={submittedModalOpen}
                onClose={() => setSubmittedModalOpen(false)}
                confirmButtonText={isICS2 ? 'Back to manifest' : 'Back to job'}
                onConfirm={async () => {
                    // An ENS has no job of its own - it is generated from a manifest and belongs to
                    // that one manifest - so it goes back to the ENS listing it was opened from.
                    if (isICS2) {
                        const sourceManifestId = declaration?.ics2ENSDeclaration?.sourceManifestId;
                        if (!sourceManifestId) return;
                        setSubmittedModalOpen(false);
                        navigate(`/manifests/${sourceManifestId}/${ManifestTabKeys.ENS}`);
                        return;
                    }

                    if (!declaration?.jobId) return;
                    const jobInfo = await getJob(declaration.jobId);
                    const isManifest = Boolean(jobInfo?.manifest?.id);
                    const jobUrl = isManifest
                        ? `/manifests/${jobInfo?.manifest?.id}/${jobInfo.id}`
                        : `/jobs/${jobInfo.id}`;
                    setSubmittedModalOpen(false);
                    navigate(jobUrl);
                }}
            />
        </>
    );
};

export default DeclarationPage;
